<template>
  <div class="merchant-container">
    <loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true"></loading>
    <div class="side-nav">
      <MerchantSideNav />
      <MenuToggle />
    </div>
    <div class="merchant-body transaction-body">
      <header>
        <MerchantHeader />
      </header>
      <div class="dashboard-content">
        <div class="row align-items-center mt-5">
          <h3>Statement</h3>
          <span class="material-icons mx-3"> arrow_forward_ios </span>
          <h3>Amej Pay</h3>
        </div>

        <!-- SUMMARY -->
        <div class="row mb-3">
          <div class="col-12 col-md-6 col-xl-3 mb-3 pl-0">
            <div class="card-style st-kpi">
              <span>Wallet Balance</span>
              <h2>{{ money(wallet.balance, currency) }}</h2>
              <small class="muted">{{ rangeLabel }}</small>
            </div>
          </div>
          <div class="col-12 col-md-6 col-xl-3 mb-3 pl-0">
            <div class="card-style st-kpi">
              <span>Money In</span>
              <h2 class="credit">+{{ money(summary.total_credit, currency) }}</h2>
              <small class="muted">{{ num(summary.count) }} entries in range</small>
            </div>
          </div>
          <div class="col-12 col-md-6 col-xl-3 mb-3 pl-0">
            <div class="card-style st-kpi">
              <span>Money Out</span>
              <h2 class="debit">&minus;{{ money(summary.total_debit, currency) }}</h2>
              <small class="muted">fees and payouts included</small>
            </div>
          </div>
          <div class="col-12 col-md-6 col-xl-3 mb-3 pl-0">
            <div class="card-style st-kpi">
              <span>Net Movement</span>
              <h2 :class="netClass">{{ signed(summary.net, currency) }}</h2>
              <small class="muted">
                Opening {{ money(summary.opening_balance, currency) }}
                &rarr; Closing {{ money(summary.closing_balance, currency) }}
              </small>
            </div>
          </div>
        </div>

        <!-- FILTERS -->
        <div class="row align-items-center">
          <div class="mb-3 col-12 col-lg-3 col-xl-2">
            <b-form-select v-model="filters.period" :options="periodOptions"></b-form-select>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-2">
            <b-form-select v-model="filters.direction" :options="directionOptions"></b-form-select>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-2">
            <b-form-select v-model="filters.type" :options="typeOptions"></b-form-select>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-2">
            <b-form-select v-model="filters.status" :options="statusOptions"></b-form-select>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-2" v-if="filters.period === 'custom'">
            <b-form-input v-model="filters.start" type="date" placeholder="From"></b-form-input>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-2" v-if="filters.period === 'custom'">
            <b-form-input v-model="filters.end" type="date" placeholder="To"></b-form-input>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-2">
            <b-form-input
              v-model="filters.search"
              type="search"
              placeholder="Search remarks, code.."
              @keyup.enter="applyFilters"
            ></b-form-input>
          </div>
          <div class="mb-3 col-12 col-lg-2 col-xl-1">
            <b-button variant="primary" @click="applyFilters">Submit</b-button>
          </div>
          <div class="mb-3 col-12 col-lg-2 col-xl-1">
            <b-button variant="light" @click="resetFilters">Reset</b-button>
          </div>
          <div class="mb-3 col-12 col-lg-3 col-xl-2">
            <a href="#" class="download-link" @click.prevent="exportCsv">
              <span class="material-icons">download</span>
              <span>{{ isExporting ? "Preparing..." : "CSV" }}</span>
            </a>
            <a href="#" class="download-link ml-3" @click.prevent="exportPdf">
              <span class="material-icons">picture_as_pdf</span>
              <span>{{ isPdfSaving ? "Preparing..." : "PDF" }}</span>
            </a>
          </div>
        </div>

        <!-- LEDGER -->
        <div class="row pl-0">
          <div class="col-12 col-md-12 col-lg-10 col-xl-12">
            <div class="row m-0 mb-2" v-if="totalItems">
              <small class="text-muted">
                Showing {{ firstIndex }}-{{ lastIndex }} of {{ totalItems }} entries
                &middot; all amounts in {{ currency }}
                &middot; newest first
              </small>
            </div>

            <b-table
              hover
              responsive
              striped
              bordered
              outlined
              show-empty
              :items="rows"
              :fields="fields"
              :table-variant="tableVariants"
              :head-variant="headVariant"
            >
              <template #empty>
                <div class="text-center py-3">No statement entries found.</div>
              </template>

              <template #cell(date)="data">
                {{ formatDate(data.item.date) }}
              </template>

              <template #cell(title)="data">
                <div class="st-desc">{{ data.item.title || typeLabel(data.item.type) }}</div>
                <small class="text-muted d-block" v-if="data.item.remarks">{{ data.item.remarks }}</small>
                <small class="text-muted d-block" v-if="partyName(data.item.counterparty)">
                  {{ data.item.direction === 'debit' ? 'To' : 'From' }}
                  {{ partyName(data.item.counterparty) }}
                </small>
                <small class="text-muted d-block st-ref" v-if="data.item.code || data.item.reference">
                  Ref {{ data.item.code || data.item.reference }}
                </small>
              </template>

              <template #cell(type)="data">
                <span class="type-pill" :class="'t-' + (data.item.type || 'other')">
                  {{ typeLabel(data.item.type) }}
                </span>
              </template>

              <template #cell(status)="data">
                <span class="status-pill" :class="statusClass(data.item.status)">
                  {{ statusLabel(data.item.status) }}
                </span>
              </template>

              <template #cell(debit)="data">
                <span v-if="isDebit(data.item)" class="debit amount-cell">
                  {{ amountOf(data.item).toFixed(2) }}
                </span>
                <span v-else class="muted-dash">&mdash;</span>
              </template>

              <template #cell(credit)="data">
                <span v-if="!isDebit(data.item)" class="credit amount-cell">
                  {{ amountOf(data.item).toFixed(2) }}
                </span>
                <span v-else class="muted-dash">&mdash;</span>
                <!-- Fee amount only, no rate - it varies per merchant and per
                     transaction, so one row's percentage reads as a standing rate. -->
                <small class="text-muted d-block" v-if="num(data.item.fee) > 0">
                  fee {{ num(data.item.fee).toFixed(2) }}
                </small>
              </template>

              <template #cell(balance_after)="data">
                <span class="amount-cell" :class="num(data.item.balance_after) < 0 ? 'debit' : ''">
                  {{ num(data.item.balance_after).toFixed(2) }}
                </span>
              </template>

              <!-- Opening balance sits above the first entry, as on a bank statement.
                   Only on page 1: on later pages it would be the balance before a
                   different row and would simply be wrong. -->
              <template #top-row v-if="currentPage === 1 && rows.length">
                <td colspan="4" class="st-boundary">Opening Balance</td>
                <td class="text-right st-boundary">&mdash;</td>
                <td class="text-right st-boundary">&mdash;</td>
                <td class="text-right st-boundary amount-cell">
                  {{ num(summary.opening_balance).toFixed(2) }}
                </td>
              </template>

              <!-- Totals for the WHOLE filtered range, from the API summary - not just
                   the rows on this page. Labelled so the two are never confused. -->
              <template #bottom-row v-if="rows.length">
                <td colspan="4" class="st-boundary">
                  Totals for {{ rangeLabel }}
                  <small class="text-muted d-block">{{ num(summary.count) }} entries in range</small>
                </td>
                <td class="text-right st-boundary amount-cell debit">
                  {{ num(summary.total_debit).toFixed(2) }}
                </td>
                <td class="text-right st-boundary amount-cell credit">
                  {{ num(summary.total_credit).toFixed(2) }}
                </td>
                <td class="text-right st-boundary amount-cell">
                  <strong>{{ num(summary.closing_balance).toFixed(2) }}</strong>
                  <small class="text-muted d-block">closing</small>
                </td>
              </template>
            </b-table>

            <!-- Shown whenever there are rows, not only past page one. With 11 entries
                 and a page size of 20 the old `totalPages > 1` guard hid it entirely,
                 which reads as "pagination is missing". -->
            <div class="st-pager" v-if="rows.length">
              <div class="st-pagesize">
                <span>Rows per page</span>
                <b-form-select v-model="perPage" :options="pageSizeOptions" size="sm"></b-form-select>
              </div>
              <nav aria-label="Statement pagination">
                <ul class="pagination mb-0">
                  <li class="page-item" :class="currentPage <= 1 ? 'disabled' : ''">
                    <a href="#" class="page-link" @click.prevent="goToPage(currentPage - 1)">&laquo; Previous</a>
                  </li>
                  <li
                    class="page-item"
                    v-for="index in pageWindow"
                    :key="index"
                    :class="currentPage === index ? 'active' : ''"
                  >
                    <a href="#" class="page-link" @click.prevent="goToPage(index)">{{ index }}</a>
                  </li>
                  <li class="page-item" :class="currentPage >= totalPages ? 'disabled' : ''">
                    <a href="#" class="page-link" @click.prevent="goToPage(currentPage + 1)">Next &raquo;</a>
                  </li>
                </ul>
              </nav>
              <div class="st-pageinfo">Page {{ currentPage }} of {{ totalPages }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <footer>
      <MerchantFooter />
    </footer>

    <b-toast id="statement-error-toast" variant="danger" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Error</strong>
      </div>
      <div><p>{{ toastMessage || message }}</p></div>
    </b-toast>
    <b-toast id="statement-success-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Export</strong>
      </div>
      <div><p>{{ toastMessage }}</p></div>
    </b-toast>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import MerchantHeader from "./MerchantHeader.vue";
import MerchantFooter from "./MerchantFooter.vue";
import MerchantSideNav from "./MerchantSideNav.vue";
import MenuToggle from "./MenuToggle.vue";
import { mapActions, mapState } from "vuex";
import { jsPDF } from "jspdf";
import { cleanText } from "../../../utils/format";
import logoUrl from "@/assets/amej-pay.png";

/** Used only as a fallback if the API stops sending filters.available. */
const FALLBACK_FILTERS = {
  direction: ["credit", "debit"],
  type: ["load", "withdrawal", "transfer", "merchant_payment", "donation", "fee", "other"],
  status: ["completed", "pending", "failed"],
  period: ["today", "7d", "30d", "90d", "month", "year", "all"],
};

const PERIOD_LABELS = {
  today: "Today", "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days",
  month: "This month", year: "This year", all: "All time",
};

export default {
  name: "MerchantStatement",
  components: { MerchantHeader, MerchantFooter, MerchantSideNav, MenuToggle, Loading },
  data() {
    return {
      isLoading: false,
      isExporting: false,
      isPdfSaving: false,
      perPage: 20,
      pageSizeOptions: [
        { value: 20, text: "20" },
        { value: 50, text: "50" },
        { value: 100, text: "100" },
      ],
      toastMessage: "",
      filters: {
        period: "all",
        direction: "",
        type: "",
        status: "",
        start: "",
        end: "",
        search: "",
      },
      tableVariants: ["light"],
      headVariant: ["light"],
      // Bank-statement shape: money out and money in are separate columns, with the
      // running balance on the right. The counterparty is folded into the description
      // the way a bank narration works, rather than taking a column of its own.
      fields: [
        { key: "date", label: "Date", sortable: true, class: "col-date" },
        { key: "title", label: "Description", class: "col-desc" },
        { key: "type", label: "Type", sortable: true, class: "col-type" },
        { key: "status", label: "Status", class: "col-status" },
        { key: "debit", label: "Debit (−)", class: "col-money text-right" },
        { key: "credit", label: "Credit (+)", class: "col-money text-right" },
        { key: "balance_after", label: "Balance", class: "col-money text-right" },
      ],
    };
  },
  computed: {
    ...mapState(["merchantStatement", "merchantEnvironment", "message", "errormessage"]),
    statement: function () {
      const s = this.merchantStatement;
      return (s && typeof s === "object") ? s : {};
    },
    wallet: function () { return this.statement.wallet || {}; },
    summary: function () { return this.statement.summary || {}; },
    pagination: function () { return this.statement.pagination || {}; },
    rows: function () { return Array.isArray(this.statement.results) ? this.statement.results : []; },
    currency: function () { return this.wallet.currency || "USD"; },
    rangeLabel: function () {
      const r = this.statement.range || {};
      if (r.start && r.end) { return r.start + " to " + r.end; }
      return PERIOD_LABELS[r.label] || PERIOD_LABELS[this.filters.period] || "All time";
    },
    netClass: function () {
      return this.num(this.summary.net) < 0 ? "debit" : "credit";
    },

    /**
     * Dropdowns are built from the API's own filters.available, so a new transaction
     * type on the server appears here without a frontend change. FALLBACK_FILTERS only
     * applies if that block goes missing.
     */
    available: function () {
      const f = this.statement.filters || {};
      return f.available || FALLBACK_FILTERS;
    },
    periodOptions: function () {
      const list = this.available.period || FALLBACK_FILTERS.period;
      const out = list.map(function (v) { return { value: v, text: PERIOD_LABELS[v] || v }; });
      out.push({ value: "custom", text: "Custom range..." });
      return out;
    },
    directionOptions: function () {
      return [{ value: "", text: "All directions" }].concat(
        (this.available.direction || []).map(function (v) {
          return { value: v, text: v.charAt(0).toUpperCase() + v.slice(1) };
        })
      );
    },
    typeOptions: function () {
      const vm = this;
      return [{ value: "", text: "All types" }].concat(
        (this.available.type || []).map(function (v) { return { value: v, text: vm.typeLabel(v) }; })
      );
    },
    statusOptions: function () {
      const vm = this;
      return [{ value: "", text: "All statuses" }].concat(
        (this.available.status || []).map(function (v) { return { value: v, text: vm.statusLabel(v) }; })
      );
    },

    totalItems: function () { return this.pagination.count || 0; },
    offset: function () { return this.pagination.offset || 0; },
    currentPage: function () { return Math.floor(this.offset / this.perPage) + 1; },
    totalPages: function () { return Math.max(1, Math.ceil(this.totalItems / this.perPage)); },
    firstIndex: function () { return this.totalItems ? this.offset + 1 : 0; },
    lastIndex: function () { return Math.min(this.offset + this.rows.length, this.totalItems); },
    pageWindow: function () {
      const total = this.totalPages;
      const cur = this.currentPage;
      let start = Math.max(1, cur - 2);
      const end = Math.min(total, start + 4);
      start = Math.max(1, Math.min(start, end - 4));
      const out = [];
      for (let i = start; i <= end; i++) { out.push(i); }
      return out;
    },
  },
  created() {
    this.syncFromRoute();
    this.fetchList();
  },
  watch: {
    "$route.query": function () {
      this.syncFromRoute();
      this.fetchList();
    },
    // Changing the page size changes what page 1 means, so go back to the start.
    perPage: function () {
      if (this.currentPageFromRoute() > 1) { this.pushQuery(1); } else { this.fetchList(); }
    },
    merchantEnvironment: function () {
      this.goToPage(1);
    },
  },
  methods: {
    ...mapActions(["listMerchantStatement"]),

    num(v) {
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    },
    money(value, currency) {
      return (currency ? currency + " " : "") + this.num(value).toFixed(2);
    },
    /** Keeps the sign visible: +121.00 / -4.24. */
    signed(value, currency) {
      const n = this.num(value);
      const sign = n < 0 ? "−" : "+";
      return sign + (currency ? currency + " " : "") + Math.abs(n).toFixed(2);
    },
    /**
     * `signed_amount` already carries the sign, but a debit row missing it would show
     * as a credit. Fall back to the direction when it is absent.
     */
    signedOf(row) {
      if (!row) { return 0; }
      if (row.signed_amount !== undefined && row.signed_amount !== null) {
        return this.num(row.signed_amount);
      }
      const amount = Math.abs(this.num(row.amount));
      return row.direction === "debit" ? -amount : amount;
    },
    /**
     * Counterparty names arrive as first + middle + last with the middle left blank,
     * so they carry doubled spaces ("Sushil  Subedi", "amej  pay"). The store's
     * sanitizeNames only covers first_name/last_name/full_name style keys, not a bare
     * `name`, so it is collapsed here.
     */
    partyName(party) {
      if (!party) { return ""; }
      return cleanText(party.name || "");
    },
    /** Money out. `direction` is authoritative; signed_amount is the fallback. */
    isDebit(row) {
      if (!row) { return false; }
      if (row.direction) { return String(row.direction).toLowerCase() === "debit"; }
      return this.num(row.signed_amount) < 0;
    },
    /** Always the positive magnitude - the column it lands in carries the sign. */
    amountOf(row) {
      if (!row) { return 0; }
      const a = (row.amount !== undefined && row.amount !== null)
        ? this.num(row.amount)
        : Math.abs(this.num(row.signed_amount));
      return Math.abs(a);
    },
    typeLabel(type) {
      if (!type) { return "Other"; }
      return String(type).replace(/_/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    },
    statusLabel(status) {
      if (!status) { return "-"; }
      return String(status).charAt(0).toUpperCase() + String(status).slice(1);
    },
    statusClass(status) {
      const s = String(status || "").toLowerCase();
      if (["completed", "paid", "success"].indexOf(s) !== -1) { return "is-success"; }
      if (["pending", "processing"].indexOf(s) !== -1) { return "is-pending"; }
      return "is-failed";
    },
    formatDate(value) {
      if (!value) { return "-"; }
      const d = new Date(value);
      if (isNaN(d.getTime())) { return String(value); }
      return d.toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },

    currentQuery(extra) {
      const f = this.filters;
      const q = {
        period: f.period === "custom" ? "" : f.period,
        direction: f.direction,
        type: f.type,
        status: f.status,
        start: f.period === "custom" ? f.start : "",
        end: f.period === "custom" ? f.end : "",
        search: f.search,
        environment: this.merchantEnvironment,
        limit: this.perPage,
        offset: (this.currentPageFromRoute() - 1) * this.perPage,
      };
      return Object.assign(q, extra || {});
    },
    currentPageFromRoute() {
      const p = parseInt(this.$route.query.page, 10);
      return (!p || p < 1) ? 1 : p;
    },
    syncFromRoute() {
      const q = this.$route.query || {};
      this.filters.period = q.period || (q.start || q.end ? "custom" : "all");
      this.filters.direction = q.direction || "";
      this.filters.type = q.type || "";
      this.filters.status = q.status || "";
      this.filters.start = q.start || "";
      this.filters.end = q.end || "";
      this.filters.search = q.search || "";
    },
    fetchList() {
      const vm = this;
      this.isLoading = true;
      return this.listMerchantStatement(this.currentQuery())
        .then(function () { vm.isLoading = false; })
        .catch(function () { vm.isLoading = false; });
    },
    pushQuery(page) {
      const f = this.filters;
      const q = {};
      if (page && page > 1) { q.page = page; }
      if (f.period && f.period !== "all" && f.period !== "custom") { q.period = f.period; }
      if (f.direction) { q.direction = f.direction; }
      if (f.type) { q.type = f.type; }
      if (f.status) { q.status = f.status; }
      if (f.period === "custom") {
        if (f.start) { q.start = f.start; }
        if (f.end) { q.end = f.end; }
      }
      if (f.search) { q.search = f.search; }
      const same = JSON.stringify(q) === JSON.stringify(this.$route.query || {});
      if (same) { this.fetchList(); return; }
      this.$router.push({ query: q }).catch(function () {});
    },
    applyFilters() { this.pushQuery(1); },
    resetFilters() {
      this.filters = { period: "all", direction: "", type: "", status: "", start: "", end: "", search: "" };
      this.pushQuery(1);
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) { return; }
      this.pushQuery(page);
    },

    /**
     * Pulls EVERY row matching the current filters, not just the page on screen -
     * a statement you can only export one page at a time is not a statement.
     * Pages the same endpoint silently, capped so a huge ledger cannot hang the tab.
     */
    fetchAllRows(maxRows) {
      const vm = this;
      const cap = maxRows || 5000;
      const pageSize = 200;
      const all = [];
      function next(offset) {
        return vm.listMerchantStatement(vm.currentQuery({ limit: pageSize, offset: offset, silent: true }))
          .then(function (payload) {
            if (!payload) { return all; }
            const rows = payload.results || [];
            for (let i = 0; i < rows.length; i++) { all.push(rows[i]); }
            const total = (payload.pagination && payload.pagination.count) || all.length;
            if (all.length >= Math.min(total, cap) || !rows.length) { return all; }
            return next(offset + pageSize);
          });
      }
      return next(0);
    },

    /**
     * Oldest first. The API returns newest first, which is right for a screen but wrong
     * for a statement document - a running balance only reads correctly downwards.
     * Sorted by date rather than just reversed, so it holds even if the API order changes.
     */
    chronological(rows) {
      return (rows || []).slice().sort(function (a, b) {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
    },
    csvCell(value) {
      const text = (value === null || value === undefined) ? "" : String(value);
      return '"' + text.replace(/"/g, '""') + '"';
    },
    exportCsv() {
      if (this.isExporting) { return; }
      this.isExporting = true;
      const vm = this;
      return this.fetchAllRows().then(function (rows) {
        // A statement reads oldest -> newest so the running balance accumulates down
        // the page. The API returns newest first, so the full set is reversed here.
        const ordered = vm.chronological(rows);
        const head = ["Date", "Code", "Type", "Status", "Description", "Remarks",
                      "Counterparty", "Counterparty Email",
                      "Debit", "Credit", "Fee", "Net Amount", "Balance", "Currency", "Reference"];
        const lines = [head.map(vm.csvCell).join(",")];
        lines.push(["", "", "", "", "Opening Balance", "", "", "", "", "", "", "",
                    vm.num(vm.summary.opening_balance).toFixed(2), vm.currency, ""]
                   .map(vm.csvCell).join(","));
        ordered.forEach(function (r) {
          const cp = r.counterparty || {};
          const debit = vm.isDebit(r) ? vm.amountOf(r).toFixed(2) : "";
          const credit = vm.isDebit(r) ? "" : vm.amountOf(r).toFixed(2);
          lines.push([
            r.date, r.code || r.id, r.type, r.status,
            r.title, r.remarks, vm.partyName(cp), cp.email || "",
            debit, credit,
            vm.num(r.fee).toFixed(2),
            vm.num(r.net_amount).toFixed(2), vm.num(r.balance_after).toFixed(2),
            vm.currency, r.reference || "",
          ].map(vm.csvCell).join(","));
        });
        lines.push(["", "", "", "", "Totals / Closing Balance", "", "", "",
                    vm.num(vm.summary.total_debit).toFixed(2),
                    vm.num(vm.summary.total_credit).toFixed(2),
                    "", "", vm.num(vm.summary.closing_balance).toFixed(2), vm.currency, ""]
                   .map(vm.csvCell).join(","));
        // BOM so Excel opens UTF-8 correctly instead of mangling accented names.
        const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "amejpay-statement-" + new Date().toISOString().slice(0, 10) + ".csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        vm.isExporting = false;
        vm.toastMessage = "Exported " + rows.length + " entries.";
        vm.$bvToast.show("statement-success-toast");
      }).catch(function () {
        vm.isExporting = false;
        vm.toastMessage = "Could not build the CSV. Please try again.";
        vm.$bvToast.show("statement-error-toast");
      });
    },

    loadLogo() {
      return new Promise(function (resolve) {
        try {
          const img = new Image();
          img.onload = function () { resolve(img); };
          img.onerror = function () { resolve(null); };
          img.src = logoUrl;
        } catch (e) { resolve(null); }
      });
    },
    exportPdf() {
      if (this.isPdfSaving) { return; }
      this.isPdfSaving = true;
      const vm = this;
      return Promise.all([this.fetchAllRows(1000), this.loadLogo()])
        .then(function (res) {
          vm.buildStatementPdf(res[0], res[1]);
          vm.isPdfSaving = false;
        })
        .catch(function () {
          vm.isPdfSaving = false;
          vm.toastMessage = "Could not build the PDF. Please try again.";
          vm.$bvToast.show("statement-error-toast");
        });
    },
    buildStatementPdf(rows, logo) {
      const doc = new jsPDF("l", "pt", "a4");
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const left = 36;
      const right = pageW - 36;
      const vm = this;
      let y = 56;

      if (logo) {
        try {
          const w = 70;
          doc.addImage(logo, "PNG", left, 28, w, Math.round(w * (106 / 198)));
        } catch (e) { /* carry on without it */ }
      }
      doc.setFontSize(16);
      doc.setTextColor(20, 20, 20);
      doc.text("Account Statement", right, y - 12, { align: "right" });
      doc.setFontSize(9);
      doc.setTextColor(110, 110, 110);
      doc.text(this.rangeLabel + "   |   All amounts in " + this.currency, right, y + 2, { align: "right" });

      // Summary strip
      y += 34;
      doc.setFontSize(10);
      const cells = [
        ["Opening Balance", this.num(this.summary.opening_balance).toFixed(2)],
        ["Total Debit", this.num(this.summary.total_debit).toFixed(2)],
        ["Total Credit", this.num(this.summary.total_credit).toFixed(2)],
        ["Net Movement", this.signed(this.summary.net, "")],
        ["Closing Balance", this.num(this.summary.closing_balance).toFixed(2)],
      ];
      let cx = left;
      cells.forEach(function (c) {
        doc.setTextColor(120, 120, 120);
        doc.text(c[0], cx, y);
        doc.setTextColor(20, 20, 20);
        doc.text(String(c[1]), cx, y + 14);
        cx += 150;
      });

      y += 30;
      doc.setDrawColor(200, 200, 200);
      doc.line(left, y, right, y);

      // Columns: money OUT and money IN are separate, balance on the right.
      const cDate = left;
      const cDesc = left + 92;
      const cType = left + 366;
      const cStat = left + 452;
      const cDeb = left + 620;
      const cCre = left + 700;
      const cBal = left + 790;

      function header() {
        y += 18;
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120);
        doc.text("Date", cDate, y);
        doc.text("Description", cDesc, y);
        doc.text("Type", cType, y);
        doc.text("Status", cStat, y);
        doc.text("Debit", cDeb, y, { align: "right" });
        doc.text("Credit", cCre, y, { align: "right" });
        doc.text("Balance", cBal, y, { align: "right" });
        y += 6;
        doc.setDrawColor(230, 230, 230);
        doc.line(left, y, right, y);
      }
      header();

      // Opening balance line
      y += 16;
      doc.setFontSize(8.5);
      doc.setTextColor(60, 60, 60);
      doc.text("Opening Balance", cDesc, y);
      doc.text(this.num(this.summary.opening_balance).toFixed(2), cBal, y, { align: "right" });

      // Oldest first, so the balance column accumulates down the page.
      this.chronological(rows).forEach(function (r) {
        y += 16;
        if (y > pageH - 46) { doc.addPage(); y = 50; header(); doc.setFontSize(8.5); }
        doc.setTextColor(60, 60, 60);
        doc.text(String(vm.formatDate(r.date)), cDate, y);

        let desc = String(r.title || vm.typeLabel(r.type));
        const party = vm.partyName(r.counterparty);
        if (party) { desc += (vm.isDebit(r) ? " to " : " from ") + party; }
        else if (r.remarks) { desc += " - " + r.remarks; }
        doc.text(desc.length > 58 ? desc.slice(0, 55) + "..." : desc, cDesc, y);

        doc.text(String(vm.typeLabel(r.type)), cType, y);
        doc.text(String(vm.statusLabel(r.status)), cStat, y);

        if (vm.isDebit(r)) {
          doc.setTextColor(190, 60, 60);
          doc.text(vm.amountOf(r).toFixed(2), cDeb, y, { align: "right" });
        } else {
          doc.setTextColor(30, 130, 75);
          doc.text(vm.amountOf(r).toFixed(2), cCre, y, { align: "right" });
        }
        doc.setTextColor(60, 60, 60);
        doc.text(vm.num(r.balance_after).toFixed(2), cBal, y, { align: "right" });
      });

      // Closing / totals line
      y += 10;
      if (y > pageH - 60) { doc.addPage(); y = 60; }
      doc.setDrawColor(200, 200, 200);
      doc.line(left, y, right, y);
      y += 16;
      doc.setFontSize(9);
      doc.setTextColor(20, 20, 20);
      doc.text("Totals / Closing Balance", cDesc, y);
      doc.setTextColor(190, 60, 60);
      doc.text(this.num(this.summary.total_debit).toFixed(2), cDeb, y, { align: "right" });
      doc.setTextColor(30, 130, 75);
      doc.text(this.num(this.summary.total_credit).toFixed(2), cCre, y, { align: "right" });
      doc.setTextColor(20, 20, 20);
      doc.text(this.num(this.summary.closing_balance).toFixed(2), cBal, y, { align: "right" });

      y += 24;
      if (y > pageH - 40) { doc.addPage(); y = 50; }
      doc.setFontSize(8);
      doc.setTextColor(140, 140, 140);
      doc.text("Generated " + new Date().toLocaleString() + ". Debit reduces the balance, credit increases it.",
               left, y);

      doc.save("amejpay-statement-" + new Date().toISOString().slice(0, 10) + ".pdf");
    },
  },
};
</script>

<style scoped>
/* Bank-statement table: numbers right-aligned and tabular so columns line up. */
.amount-cell {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  white-space: nowrap;
}
.muted-dash { color: #cbd2dc; }
.st-desc { font-weight: 500; }
.st-ref { font-size: 11px; opacity: .8; }
.st-boundary {
  background: #f7f9fc;
  font-weight: 600;
  color: #2c3e50;
  border-top: 2px solid #e4e9f0 !important;
}
.st-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 6px;
}
.st-pagesize { display: flex; align-items: center; font-size: 13px; color: #6b7280; }
.st-pagesize span { margin-right: 8px; white-space: nowrap; }
.st-pagesize select { width: auto !important; min-width: 76px; }
.st-pageinfo { font-size: 13px; color: #6b7280; }
.st-kpi span { color: #8a94a6; font-size: 13px; }
.st-kpi h2 { margin: 6px 0 2px; font-size: 24px; }
.st-kpi .muted { color: #8a94a6; font-size: 12px; }
.credit { color: #1e8c50; }
.debit { color: #c83c3c; }
.download-link { text-decoration: none; color: #0066b3; display: inline-flex; align-items: center; }
.download-link .material-icons { font-size: 18px; margin-right: 4px; }
.status-pill, .type-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.status-pill.is-success { background: #e7f6ed; color: #1e8c50; }
.status-pill.is-pending { background: #fff5e0; color: #9a6b00; }
.status-pill.is-failed  { background: #fdeaea; color: #c83c3c; }
.type-pill { background: #eef2f7; color: #4a5568; font-weight: 500; }
.type-pill.t-merchant_payment { background: #e7f0fb; color: #0066b3; }
.type-pill.t-fee { background: #fdeaea; color: #c83c3c; }
.type-pill.t-withdrawal, .type-pill.t-transfer { background: #f4eefb; color: #6b46a8; }
.type-pill.t-load { background: #e7f6ed; color: #1e8c50; }
</style>
